import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { ICustomerForecastTimelineFilterContentItem } from '../domains/entities/customerForecastTimelineState';
import { MAX_OF_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT, MIN_OF_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT } from '../constants';
import { ICustomerForecastDiffLastMonthBusinessData } from '../domains/entities/customerForecastDiffLastMonth';

/**
 * 客先内示遍歴のViewModel
 */
export class CustomerForecastTimelineViewModel {
  public DEFAULT_CUSTOMER_FORECAST_TIMELINE_FILTERS: ReadonlyArray<Readonly<ICustomerForecastTimelineFilterContentItem>> = [
    {
      filterType: 'FORECAST_TIMELINE_STAFF_NAME',
      selectedValue: '',
      label: '担当者',
    },
    {
      filterType: 'FORECAST_TIMELINE_WAREHOUSE_CODE',
      selectedValue: '',
      label: '置場CD',
    },
    {
      filterType: 'FORECAST_TIMELINE_WAREHOUSE',
      selectedValue: '',
      label: '置場名',
    },
    {
      filterType: 'FORECAST_TIMELINE_SUPPLIER_CODE',
      selectedValue: '',
      label: '仕入先CD',
    },
    {
      filterType: 'FORECAST_TIMELINE_SUPPLIER_NAME',
      selectedValue: '',
      label: '仕入先名',
    },
    {
      filterType: 'FORECAST_TIMELINE_BUSINESS_CODE',
      selectedValue: '',
      label: '商品CD',
    },
    {
      filterType: 'FORECAST_TIMELINE_BUSINESS_NAME',
      selectedValue: '',
      label: '商品名',
    },
    {
      filterType: 'FORECAST_TIMELINE_PACKAGE_AMOUNT',
      selectedValue: '',
      label: '容量',
    },
    {
      filterType: 'FORECAST_TIMELINE_CUSTOMER_CODE',
      selectedValue: '',
      label: '売上先CD',
    },
    {
      filterType: 'FORECAST_TIMELINE_CUSTOMER_NAME',
      selectedValue: '',
      label: '売上先名',
    },
    {
      filterType: 'FORECAST_TIMELINE_SALES_DESTINATION_CODE',
      selectedValue: '',
      label: '送り先CD',
    },
    {
      filterType: 'FORECAST_TIMELINE_SALES_DESTINATION_NAME',
      selectedValue: '',
      label: '送り先名',
    },
  ];

  /**
   * 内示遍歴の表示月数の選択肢
   * 12ヶ月~24ヶ月で固定
   */
  public MONTH_OPTIONS_DISPLAY_FORECAST: ReadonlyArray<number> = Array(
    MAX_OF_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT - MIN_OF_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT + 1
  )
    .fill(0)
    .map((_, index) => MIN_OF_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT + index);

  /**
   * フィルター適用ボタンの非活性状態を取得
   * @param filterContentsInfos - フィルターに使用する項目の情報
   */
  public getIsDisabledApplyFilterButton = (filterContentsInfos: ICustomerForecastTimelineFilterContentItem[]): boolean => {
    // 未選択の項目が1つでもあればボタンを無効化
    // TODO: 現状、「担当者」は存在しないので、これは除外する
    return filterContentsInfos.some(
      (filterContentInfo) => filterContentInfo.selectedValue === '' && filterContentInfo.filterType !== 'FORECAST_TIMELINE_STAFF_NAME'
    );
  };

  /**
   * 受領開始年月を取得
   * @param targetStartYearMonth - 開始年月
   */
  public getReceivedStartYearMonth = (targetStartYearMonth: IYearMonth): IYearMonth => {
    const { updateYear: year, updateMonth: month } = DateService.updateYearMonth(targetStartYearMonth.year, targetStartYearMonth.month, -3);
    return { year, month };
  };

  /**
   *　開始対象年月を表示月数から計算
   * @param displayForecastsMonth - 表示月数
   */
  public calculateTargetStartYearMonthFromMonths = (displayForecastsMonth: number): IYearMonth => {
    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    // 終点を計算（当月 + 3ヶ月）
    const { updateYear: endYear, updateMonth: endMonth } = DateService.updateYearMonth(currentYearMonth.year, currentYearMonth.month, 3);
    // 始点を計算（終点 - 表示年月 - 1ヶ月）
    const { updateYear: startYear, updateMonth: startMonth } = DateService.updateYearMonth(endYear, endMonth, -displayForecastsMonth - 1);
    return {
      year: startYear,
      month: startMonth,
    };
  };

  /**
   * 対象年月のリストを取得する
   * @param targetStartYearMonth - 開始年月
   * @param displayForecastsMonth - 表示する内示の月数
   */
  public getTargetYearMonthList = (targetStartYearMonth: IYearMonth, displayForecastsMonth: number): IYearMonth[] => {
    const targetYearMonthList: IYearMonth[] = [];
    const { year: targetYear, month: targetMonth } = targetStartYearMonth;
    for (let i = 0; i < displayForecastsMonth + 1; i++) {
      const { updateYear: year, updateMonth: month } = DateService.updateYearMonth(targetYear, targetMonth, i);
      targetYearMonthList.push({ year, month });
    }
    return targetYearMonthList;
  };

  /**
   * 受領年月のリストを取得する
   * @param targetStartYearMonth - 開始年月
   * @param displayForecastsMonth - 表示する内示の月数
   */
  public getReceivedYearMonthList = (targetStartYearMonth: IYearMonth, displayForecastsMonth: number): IYearMonth[] => {
    const { year: startYear, month: startMonth } = this.getReceivedStartYearMonth(targetStartYearMonth);
    const receivedYearMonthList: IYearMonth[] = [];
    for (let i = 0; i < displayForecastsMonth + 1; i++) {
      const { updateYear: year, updateMonth: month } = DateService.updateYearMonth(startYear, startMonth, i);
      receivedYearMonthList.push({ year, month });
    }
    return receivedYearMonthList;
  };

  /**
   * 内示遍歴の表示月数の選択肢を取得
   * @param index - インデックス
   */
  public getMonthDisplayForecastByIndex = (index: number): number => {
    return this.MONTH_OPTIONS_DISPLAY_FORECAST[index];
  };

  /**
   * 開始対象年月が無効かどうかを取得
   * @param targetStartYearMonth - 開始年月
   */
  public isDisabledTargetStartYearMonth = (targetStartYearMonth: IYearMonth): boolean => {
    // 有効範囲は(当月の24ヶ月前)以上、(当月の1ヶ月先)以下
    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    const { updateYear: _headYear, updateMonth: _headMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      -24
    );
    const headYearMonth = { year: _headYear, month: _headMonth };
    const { updateYear: _tailYear, updateMonth: _tailMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      1
    );
    const tailYearMonth = { year: _tailYear, month: _tailMonth };
    const betweenYearMonths = DateService.getYearMonthsBetween(headYearMonth, tailYearMonth);
    // 有効範囲外の場合は無効
    return !betweenYearMonths.some(
      (yearMonth) => yearMonth.year === targetStartYearMonth.year && yearMonth.month === targetStartYearMonth.month
    );
  };

  /**
   * 取引情報をもとに、値がセットされた状態のフィルタ情報を取得する
   * @param businessData - 取引情報
   */
  public getSetFilterContents = (
    businessData: ICustomerForecastDiffLastMonthBusinessData
  ): ICustomerForecastTimelineFilterContentItem[] => {
    return this.DEFAULT_CUSTOMER_FORECAST_TIMELINE_FILTERS.map((filterContent) => {
      switch (filterContent.filterType) {
        case 'FORECAST_TIMELINE_STAFF_NAME':
          return {
            ...filterContent,
            selectedValue: businessData.customerForecastStaffList[0]?.name ?? businessData.customerForecastStaffList[0]?.email ?? '',
          };
        case 'FORECAST_TIMELINE_WAREHOUSE_CODE':
          return {
            ...filterContent,
            selectedValue: businessData.warehouseNumber,
          };
        case 'FORECAST_TIMELINE_WAREHOUSE':
          return {
            ...filterContent,
            selectedValue: businessData.warehouse,
          };
        case 'FORECAST_TIMELINE_SUPPLIER_CODE':
          return {
            ...filterContent,
            selectedValue: businessData.supplierNumber,
          };
        case 'FORECAST_TIMELINE_SUPPLIER_NAME':
          return {
            ...filterContent,
            selectedValue: businessData.supplierName,
          };
        case 'FORECAST_TIMELINE_BUSINESS_CODE':
          return {
            ...filterContent,
            selectedValue: businessData.businessNumber,
          };
        case 'FORECAST_TIMELINE_BUSINESS_NAME':
          return {
            ...filterContent,
            selectedValue: businessData.businessName,
          };
        case 'FORECAST_TIMELINE_PACKAGE_AMOUNT':
          return {
            ...filterContent,
            selectedValue: businessData.package,
          };
        case 'FORECAST_TIMELINE_CUSTOMER_CODE':
          return {
            ...filterContent,
            selectedValue: businessData.customerNumber,
          };
        case 'FORECAST_TIMELINE_CUSTOMER_NAME':
          return {
            ...filterContent,
            selectedValue: businessData.customerName,
          };
        case 'FORECAST_TIMELINE_SALES_DESTINATION_CODE':
          return {
            ...filterContent,
            selectedValue: businessData.salesDestinationNumber,
          };
        case 'FORECAST_TIMELINE_SALES_DESTINATION_NAME':
          return {
            ...filterContent,
            selectedValue: businessData.salesDestinationName,
          };
        default:
          return filterContent;
      }
    });
  };

  /**
   * フィルタ情報をクリアする
   */
  public clearFilterContents = (): ICustomerForecastTimelineFilterContentItem[] => {
    return this.DEFAULT_CUSTOMER_FORECAST_TIMELINE_FILTERS.map((filterContent) => {
      return {
        ...filterContent,
        selectedValue: '',
      };
    });
  };

  /**
   * 開始対象年月が無効かどうかを取得
   * @param selectedYearMonth - 選択された開始年月
   * @param endYearMonth - 終了年月
   */
  public isDisabledStartYearMonthDownloadModal = (selectedYearMonth: IYearMonth, endYearMonth?: IYearMonth): boolean => {
    // 有効範囲は(当月の120ヶ月前)以上、(当月の24ヶ月先)以下
    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    const { updateYear: _headYear, updateMonth: _headMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      -120
    );
    const headYearMonth = { year: _headYear, month: _headMonth };
    const { updateYear: _tailYear, updateMonth: _tailMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      24
    );
    const tailYearMonth = { year: _tailYear, month: _tailMonth };
    const betweenYearMonths = DateService.getYearMonthsBetween(headYearMonth, tailYearMonth);
    // 有効範囲外の場合は無効かつ、終了年月よりも後の年月を選択している場合は無効
    return (
      !betweenYearMonths.some((yearMonth) => yearMonth.year === selectedYearMonth.year && yearMonth.month === selectedYearMonth.month) ||
      (endYearMonth !== undefined && DateService.isAfterYearMonth(selectedYearMonth, endYearMonth))
    );
  };

  /**
   * 終了年月が無効かどうかを取得
   * @param selectedYearMonth - 選択された終了年月
   * @param startYearMonth - 開始年月
   */
  public isDisabledEndYearMonthDownloadModal = (selectedYearMonth: IYearMonth, startYearMonth?: IYearMonth): boolean => {
    // 有効範囲は(当月の120ヶ月前)以上、(当月の24ヶ月先)以下
    const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
    const { updateYear: _headYear, updateMonth: _headMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      -120
    );
    const headYearMonth = { year: _headYear, month: _headMonth };
    const { updateYear: _tailYear, updateMonth: _tailMonth } = DateService.updateYearMonth(
      currentYearMonth.year,
      currentYearMonth.month,
      24
    );
    const tailYearMonth = { year: _tailYear, month: _tailMonth };
    const betweenYearMonths = DateService.getYearMonthsBetween(headYearMonth, tailYearMonth);
    // 有効範囲外の場合は無効かつ、開始年月よりも前の年月を選択している場合は無効
    return (
      !betweenYearMonths.some((yearMonth) => yearMonth.year === selectedYearMonth.year && yearMonth.month === selectedYearMonth.month) ||
      (startYearMonth !== undefined && DateService.isBeforeYearMonth(selectedYearMonth, startYearMonth))
    );
  };

  /**
   * 終了年月を計算
   * @param startYearMonth - 開始年月
   * @param range - 月数
   */
  public calculateEndYearMonth = (startYearMonth: IYearMonth, range: number): IYearMonth => {
    const { updateYear: year, updateMonth: month } = DateService.updateYearMonth(startYearMonth.year, startYearMonth.month, range);
    return { year, month };
  };
}
